"use client";

import { motion } from "framer-motion";
import AnimatedSection from "./AnimatedSection";

const TIERS = [
  {
    name: "Marketing Site",
    price: "$2,500 – $6,000",
    timeline: "2–4 weeks",
    description:
      "The site your business actually needs. Home, about, services, contact, maybe a blog. Fast, mobile-first, SEO-ready. Built to convert visitors into leads.",
    includes: [
      "5–10 pages, custom design",
      "Mobile-first, responsive",
      "Contact forms + basic SEO",
      "CMS setup (you can edit content)",
      "Deployed to Vercel, SSL included",
    ],
    example: "SNG Woodwork, O'Connor Land Works",
    accent: "emerald",
    popular: false,
  },
  {
    name: "Web Application",
    price: "$8,000 – $18,000",
    timeline: "6–10 weeks",
    description:
      "Full app with accounts, database, and real functionality. Auth, dashboards, payments, admin panels — the kind of product people log into daily.",
    includes: [
      "User auth + accounts",
      "Database + real-time sync",
      "Stripe payments or subscriptions",
      "Admin dashboard",
      "API design + documentation",
    ],
    example: "BidFlow, PantryBook, Alpha Lab",
    accent: "teal",
    popular: true,
  },
  {
    name: "Mobile + Web",
    price: "$12,000 – $22,000",
    timeline: "8–12 weeks",
    description:
      "One codebase, two platforms. Web app plus native iOS/Android via Capacitor. App Store submission included. Push notifications, camera access, offline mode.",
    includes: [
      "Everything in Web Application",
      "Native iOS build via Capacitor",
      "App Store submission",
      "Push notifications",
      "Camera, GPS, native gestures",
    ],
    example: "Alpha Lab (iOS)",
    accent: "violet",
    popular: false,
  },
];

const accentClasses: Record<string, { border: string; badge: string; text: string; bg: string }> = {
  emerald: {
    border: "border-emerald-500/20",
    badge: "bg-emerald-500/10 text-emerald-400 ring-emerald-500/20",
    text: "text-emerald-400",
    bg: "from-emerald-500/10 to-transparent",
  },
  teal: {
    border: "border-teal-500/20",
    badge: "bg-teal-500/10 text-teal-400 ring-teal-500/20",
    text: "text-teal-400",
    bg: "from-teal-500/10 to-transparent",
  },
  violet: {
    border: "border-violet-500/20",
    badge: "bg-violet-500/10 text-violet-400 ring-violet-500/20",
    text: "text-violet-400",
    bg: "from-violet-500/10 to-transparent",
  },
};

const Pricing = () => {
  return (
    <section id="pricing" className="relative px-4 py-24">
      <div className="section-divider mx-auto mb-20 max-w-xl" />

      <div className="mx-auto max-w-6xl">
        <AnimatedSection className="mb-16 text-center">
          <p className="mb-3 text-sm font-medium uppercase tracking-[0.2em] text-emerald-400/80 font-[family-name:var(--font-jetbrains-mono)]">
            Investment
          </p>
          <h2 className="font-[family-name:var(--font-space-grotesk)] text-3xl font-bold md:text-5xl">
            What It <span className="gradient-text">Actually Costs</span>
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-[#94A3B8]">
            No hourly billing, no surprise invoices. Fixed-scope, fixed-price.
            If scope changes, we talk about it first.
          </p>
        </AnimatedSection>

        <div className="grid gap-6 md:grid-cols-3">
          {TIERS.map((tier, i) => {
            const colors = accentClasses[tier.accent];
            return (
              <AnimatedSection key={tier.name} delay={i * 0.1}>
                <motion.div
                  whileHover={{ y: -6 }}
                  className={`glass-card relative flex h-full flex-col rounded-2xl ring-1 ${colors.border} ${tier.popular ? "ring-2" : "ring-white/[0.06]"} overflow-hidden`}
                >
                  {/* Popular badge */}
                  {tier.popular && (
                    <div className="absolute right-4 top-4 rounded-full bg-teal-500/10 px-3 py-1 text-[10px] font-medium uppercase tracking-wider text-teal-400 ring-1 ring-teal-500/20">
                      Most Common
                    </div>
                  )}

                  {/* Gradient top */}
                  <div className={`h-1.5 bg-gradient-to-r ${colors.bg}`} />

                  <div className="flex flex-1 flex-col p-8">
                    <h3 className="font-[family-name:var(--font-space-grotesk)] text-xl font-bold text-white">
                      {tier.name}
                    </h3>

                    <div className="mt-3">
                      <span className={`text-2xl font-bold ${colors.text}`}>
                        {tier.price}
                      </span>
                    </div>

                    <p className="mt-1 text-xs text-[#94A3B8]/60 font-[family-name:var(--font-jetbrains-mono)]">
                      {tier.timeline}
                    </p>

                    <p className="mt-4 text-sm leading-relaxed text-[#94A3B8]">
                      {tier.description}
                    </p>

                    <ul className="mt-6 flex-1 space-y-3">
                      {tier.includes.map((item) => (
                        <li
                          key={item}
                          className="flex items-start gap-2.5 text-sm text-white/70"
                        >
                          <svg
                            className={`mt-0.5 h-4 w-4 shrink-0 ${colors.text}`}
                            viewBox="0 0 16 16"
                            fill="none"
                          >
                            <path
                              d="M3 8.5L6.5 12L13 4"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          {item}
                        </li>
                      ))}
                    </ul>

                    <div className="mt-6 border-t border-white/[0.06] pt-5">
                      <p className="text-xs text-[#94A3B8]/50">
                        Example: {tier.example}
                      </p>
                    </div>
                  </div>
                </motion.div>
              </AnimatedSection>
            );
          })}
        </div>

        {/* Note */}
        <AnimatedSection delay={0.4} className="mt-10 text-center">
          <p className="text-sm text-[#94A3B8]/60">
            AI features, custom integrations, or complex workflows — add{" "}
            <span className="text-white/80">$5,000 – $15,000</span> depending on
            scope. Every project starts with a free 30-min call to nail down the
            exact number.
          </p>
        </AnimatedSection>
      </div>
    </section>
  );
};

export default Pricing;
